import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { MapPin, Users, Bed, Bath, Calendar, ArrowLeft, Check, MessageCircle, X, Send, Loader, Home } from 'lucide-react';
import { apartmentService, messageService } from '../services/api';
import { Apartment } from '../types';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

const ApartmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [apartment, setApartment] = useState<Apartment | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const { isAuthenticated, user } = useAuthStore();
  
  // Contact modal
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [contactSubject, setContactSubject] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fetchApartment = async () => {
      if (!id) return;
      try {
        const res = await apartmentService.getById(parseInt(id));
        setApartment(res.data.apartment);
      } catch (error) { console.error(error); }
      finally { setLoading(false); }
    };
    fetchApartment();
  }, [id]);

  const formatPrice = (price: number) => new Intl.NumberFormat('fr-FR').format(price);
  const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

  const dateDebut = searchParams.get('date_debut') || '';
  const dateFin = searchParams.get('date_fin') || '';
  const nbPersonnes = searchParams.get('nb_personnes') || '';

  const bookingQuery = new URLSearchParams({
    ...(dateDebut ? { date_debut: dateDebut } : {}),
    ...(dateFin ? { date_fin: dateFin } : {}),
    ...(nbPersonnes ? { nb_personnes: nbPersonnes } : {})
  }).toString();

  const handleContactOwner = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactMessage.trim() || !id) return;
    
    setSending(true);
    try {
      await messageService.contactOwner(parseInt(id), {
        contenu: contactMessage.trim(),
        sujet: contactSubject.trim() || undefined
      });
      toast.success('Message envoyé au gérant !');
      setShowContactModal(false);
      setContactMessage('');
      setContactSubject('');
      // Rediriger vers les messages si connecté
      if (isAuthenticated && user?.role === 'ROLE_LOCATAIRE') {
        navigate('/locataire/messages');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const openContactModal = () => {
    if (!isAuthenticated) {
      toast.error('Connectez-vous pour contacter le gérant');
      navigate('/login');
      return;
    }
    setShowContactModal(true);
  };

  if (loading) return <div className="section"><div className="container-app"><div className="animate-pulse space-y-4"><div className="h-96 bg-neutral-200 rounded-xl"></div><div className="h-8 bg-neutral-200 rounded w-1/2"></div></div></div></div>;
  if (!apartment) return <div className="section text-center"><p>Appartement non trouvé</p><Link to="/apartments" className="btn btn-primary mt-4">Retour</Link></div>;

  const images = apartment.photos && apartment.photos.length > 0 ? apartment.photos : [{ url: '', id: 0 }];

  return (
    <div className="section">
      <div className="container-app">
        <Link to="/apartments" className="inline-flex items-center text-neutral-600 hover:text-primary-600 mb-6"><ArrowLeft className="w-4 h-4 mr-2" />Retour aux appartements</Link>
        
        {/* Images */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          <div className="aspect-[4/3] rounded-xl overflow-hidden bg-neutral-200">
            {images[selectedImage]?.url ? (
              <img src={`${baseUrl}${images[selectedImage].url}`} alt={apartment.titre} className="w-full h-full object-cover" />
            ) : <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center"><Home className="w-24 h-24 text-primary-400" /></div>}
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-3 gap-2 auto-rows-max content-start overflow-y-auto max-h-[300px] lg:max-h-[500px]">
              {images.map((img, idx) => (
                <button key={img.id} onClick={() => setSelectedImage(idx)} className={`relative w-full pb-[100%] rounded-lg overflow-hidden ${selectedImage === idx ? 'ring-2 ring-primary-500' : ''}`}>
                  <img src={`${baseUrl}${img.url}`} alt="" className="absolute inset-0 w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Info */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h1 className="text-3xl font-heading font-bold text-neutral-900 mb-2">{apartment.titre}</h1>
              <div className="flex items-center text-neutral-600"><MapPin className="w-5 h-5 mr-2" />{apartment.adresse}, {apartment.ville}</div>
            </div>
            <div className="flex flex-wrap gap-4 py-4 border-y border-neutral-200">
              <div className="flex items-center gap-2"><Users className="w-5 h-5 text-primary-500" /><span>{apartment.capacite} personnes</span></div>
              <div className="flex items-center gap-2"><Bed className="w-5 h-5 text-primary-500" /><span>{apartment.nb_chambres} chambres</span></div>
              <div className="flex items-center gap-2"><Bath className="w-5 h-5 text-primary-500" /><span>{apartment.nb_salles_bain} sdb</span></div>
            </div>
            <div><h2 className="text-xl font-semibold mb-3">Description</h2><p className="text-neutral-600 whitespace-pre-line">{apartment.description_longue || apartment.description_courte}</p></div>
            {apartment.equipments && apartment.equipments.length > 0 && (
              <div><h2 className="text-xl font-semibold mb-3">Équipements</h2><div className="grid grid-cols-2 md:grid-cols-3 gap-2">{apartment.equipments.map(eq => (<div key={eq.id} className="flex items-center gap-2 text-neutral-600"><Check className="w-4 h-4 text-green-500" />{eq.nom}</div>))}</div></div>
            )}
          </div>

          {/* Booking card */}
          <div className="lg:col-span-1">
            <div className="card p-6 sticky top-24">
              <div className="text-3xl font-bold text-primary-600 mb-1">{formatPrice(apartment.prix_par_nuit)} <span className="text-base font-normal text-neutral-500">FCFA/nuit</span></div>
              {(apartment.frais_menage ?? 0) > 0 && <p className="text-sm text-neutral-500 mb-4">+ {formatPrice(apartment.frais_menage!)} FCFA frais de ménage</p>}
              <Link to={`/booking/${apartment.id}${bookingQuery ? `?${bookingQuery}` : ''}`} className="btn btn-primary btn-lg w-full"><Calendar className="w-5 h-5 mr-2" />Réserver</Link>
              {!isAuthenticated && (
                <p className="text-center text-neutral-500 text-sm mt-2">Pas besoin de compte pour réserver</p>
              )}
              
              {/* Bouton contacter le gérant */}
              <button
                onClick={openContactModal}
                className="btn btn-outline btn-lg w-full mt-3 flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                Contacter le gérant
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de contact */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold text-lg">Contacter le gérant</h2>
              <button 
                onClick={() => setShowContactModal(false)} 
                className="p-1 hover:bg-neutral-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleContactOwner} className="p-4 space-y-4">
              <div className="p-3 bg-neutral-50 rounded-lg">
                <p className="text-sm text-neutral-600">
                  <span className="font-medium">Appartement :</span> {apartment.titre}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Sujet (optionnel)</label>
                <input
                  type="text"
                  value={contactSubject}
                  onChange={(e) => setContactSubject(e.target.value)}
                  className="input w-full"
                  placeholder="Ex: Question sur la disponibilité"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Votre message *</label>
                <textarea
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  className="input w-full min-h-[120px]"
                  placeholder="Bonjour, j'aimerais avoir plus d'informations sur..."
                  required
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <button 
                  type="button" 
                  onClick={() => setShowContactModal(false)} 
                  className="btn btn-outline flex-1"
                  disabled={sending}
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  disabled={sending || !contactMessage.trim()} 
                  className="btn btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {sending ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Envoyer
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
export default ApartmentDetailPage;
